export type DcmpBillingClinic = {
  id: string;
  name: string;
  hasPaymentRates: boolean;
  availableMonths: string[];
};

export type DcmpBillingClinicRef = {
  id: string;
  name: string;
};

export type DcmpBillingSummary = {
  entries: number;
  patients: number;
  netBillables: number;
  unmatchedRateEntries: number;
  unmatchedRateClinics: number;
  unmatchedRateCodes: string[];
};

export type DcmpBillingTrendPoint = {
  monthStart: string;
  patients: number;
  netBillables: number;
};

export type DcmpBillingGuideStatus = {
  status: string;
  patients: number;
};

export type DcmpBillingRateDetail = {
  patientStage: string;
  careContext: string;
  tier: string;
  hcpcs: string;
  averageRate: number;
  patients: number;
  netBillables: number;
  unmatchedRateEntries: number;
};

export type DcmpBillingWeeklyCohort = {
  weekStart: string;
  approved: number;
  inProgress: number;
  new: number;
  closed: number;
  total: number;
};

export type DcmpBillingWeeklyIneligible = {
  weekStart: string;
  coverage: number;
  clinicalProgram: number;
  residence: number;
  notRecorded: number;
  total: number;
};

export type DcmpBillingSnapshotResponse = {
  asOf: string;
  month: string;
  clinic: DcmpBillingClinicRef;
  clinics: DcmpBillingClinic[];
  availableMonths: string[];
  summary: DcmpBillingSummary;
  monthlyTrend: DcmpBillingTrendPoint[];
  guideStatuses: DcmpBillingGuideStatus[];
  rateDetails: DcmpBillingRateDetail[];
  weeklyCohorts: DcmpBillingWeeklyCohort[];
  weeklyIneligible: DcmpBillingWeeklyIneligible[];
  generatedAt: string;
  source: string;
};

export type DcmpBillingSnapshotParams = {
  clinic?: string;
  month?: string;
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isClinic(value: unknown): value is DcmpBillingClinic {
  if (!isObject(value)) return false;
  return (
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    typeof value.hasPaymentRates === "boolean" &&
    Array.isArray(value.availableMonths) &&
    value.availableMonths.every((month) => typeof month === "string")
  );
}

function isSummary(value: unknown): value is DcmpBillingSummary {
  if (!isObject(value)) return false;
  return (
    isFiniteNumber(value.entries) &&
    isFiniteNumber(value.patients) &&
    isFiniteNumber(value.netBillables) &&
    isFiniteNumber(value.unmatchedRateEntries) &&
    isFiniteNumber(value.unmatchedRateClinics) &&
    Array.isArray(value.unmatchedRateCodes) &&
    value.unmatchedRateCodes.every((code) => typeof code === "string")
  );
}

function isTrendPoint(value: unknown): value is DcmpBillingTrendPoint {
  if (!isObject(value)) return false;
  return (
    typeof value.monthStart === "string" &&
    isFiniteNumber(value.patients) &&
    isFiniteNumber(value.netBillables)
  );
}

function isGuideStatus(value: unknown): value is DcmpBillingGuideStatus {
  if (!isObject(value)) return false;
  return typeof value.status === "string" && isFiniteNumber(value.patients);
}

function isRateDetail(value: unknown): value is DcmpBillingRateDetail {
  if (!isObject(value)) return false;
  return (
    typeof value.patientStage === "string" &&
    typeof value.careContext === "string" &&
    typeof value.tier === "string" &&
    typeof value.hcpcs === "string" &&
    isFiniteNumber(value.averageRate) &&
    isFiniteNumber(value.patients) &&
    isFiniteNumber(value.netBillables) &&
    isFiniteNumber(value.unmatchedRateEntries)
  );
}

function isWeeklyCohort(value: unknown): value is DcmpBillingWeeklyCohort {
  if (!isObject(value)) return false;
  return (
    typeof value.weekStart === "string" &&
    isFiniteNumber(value.approved) &&
    isFiniteNumber(value.inProgress) &&
    isFiniteNumber(value.new) &&
    isFiniteNumber(value.closed) &&
    isFiniteNumber(value.total)
  );
}

function isWeeklyIneligible(value: unknown): value is DcmpBillingWeeklyIneligible {
  if (!isObject(value)) return false;
  return (
    typeof value.weekStart === "string" &&
    isFiniteNumber(value.coverage) &&
    isFiniteNumber(value.clinicalProgram) &&
    isFiniteNumber(value.residence) &&
    isFiniteNumber(value.notRecorded) &&
    isFiniteNumber(value.total)
  );
}

export function isDcmpBillingSnapshotResponse(
  value: unknown,
): value is DcmpBillingSnapshotResponse {
  if (!isObject(value)) return false;
  return (
    typeof value.asOf === "string" &&
    typeof value.month === "string" &&
    isObject(value.clinic) &&
    typeof value.clinic.id === "string" &&
    typeof value.clinic.name === "string" &&
    Array.isArray(value.clinics) &&
    value.clinics.every(isClinic) &&
    Array.isArray(value.availableMonths) &&
    value.availableMonths.every((month) => typeof month === "string") &&
    isSummary(value.summary) &&
    Array.isArray(value.monthlyTrend) &&
    value.monthlyTrend.every(isTrendPoint) &&
    Array.isArray(value.guideStatuses) &&
    value.guideStatuses.every(isGuideStatus) &&
    Array.isArray(value.rateDetails) &&
    value.rateDetails.every(isRateDetail) &&
    Array.isArray(value.weeklyCohorts) &&
    value.weeklyCohorts.every(isWeeklyCohort) &&
    Array.isArray(value.weeklyIneligible) &&
    value.weeklyIneligible.every(isWeeklyIneligible) &&
    typeof value.generatedAt === "string" &&
    typeof value.source === "string"
  );
}
